import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { formatDistanceToNow } from 'date-fns';
import { Headset } from '@phosphor-icons/react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * `filter` ('all' | 'unread') and `query` (a member number, or part of one)
 * narrow the list on the client. That is safe only because /api/chats returns
 * the caller's whole scope -- every thread the server lets them see, up to
 * 1000 -- so nothing outside the list is being hidden by accident.
 */
const ChatList = ({ onSelectChat, selectedChatId, filter = 'all', query = '' }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const { messages } = useSocket();
  const { user } = useAuth();

  const loadChats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chats`, {
        withCredentials: true,
      });
      setChats(data);
    } catch (error) {
      // Silent fail - UI shows empty state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  useEffect(() => {
    if (messages.length === 0) return;
    const latestMessage = messages[messages.length - 1];
    setChats(prev =>
      prev.map(chat => {
        if (chat.id !== latestMessage.chat_id) return chat;
        // Bump unread only when the message is for a thread the user isn't
        // currently viewing, and didn't come from the user themselves.
        const isOpen = chat.id === selectedChatId;
        const fromMe = latestMessage.sender_id === user?.id;
        const unread = isOpen || fromMe
          ? 0
          : (chat.unread_count || 0) + 1;
        return {
          ...chat,
          last_message: latestMessage,
          updated_at: latestMessage.created_at,
          unread_count: unread,
        };
      }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
    );
  }, [messages, selectedChatId, user]);

  // Clear the badge locally the moment a thread becomes the selected one.
  useEffect(() => {
    if (!selectedChatId) return;
    setChats(prev =>
      prev.map(chat =>
        chat.id === selectedChatId && chat.unread_count
          ? { ...chat, unread_count: 0 }
          : chat
      )
    );
  }, [selectedChatId]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">Loading chats...</p>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
        <Headset className="w-12 h-12 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No support threads yet</p>
      </div>
    );
  }

  const digits = query.replace(/\D/g, '');
  const shown = chats.filter((chat) => {
    if (filter === 'unread' && !(chat.unread_count > 0)) return false;
    if (!digits) return true;
    if (chat.member_no == null) return false;
    // "7", "07" and "007" all find No. 007.
    return String(chat.member_no).padStart(3, '0').includes(digits)
      || String(chat.member_no) === String(Number(digits));
  });

  if (shown.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-testid="chat-list-empty-filter">
        <p className="text-sm text-muted-foreground">No conversations match the current view.</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto" data-testid="chat-list">
      {shown.map(chat => (
        <div
          key={chat.id}
          onClick={() => onSelectChat(chat)}
          className={`
            px-4 py-3 border-b border-border cursor-pointer transition-colors
            hover:bg-muted/50
            ${selectedChatId === chat.id ? 'bg-muted' : ''}
          `}
          data-testid={`chat-item-${chat.id}`}
        >
          <div className="flex items-start gap-3">
            {/* The number, not a picture and not initials. Staff see members
                as numbers here for the same reason members see each other that
                way: this list is open on a train. getInitials would have made
                "No. 007" into "N", which is the worst of both -- no longer a
                name, and not yet a number. A member looking at their own one
                thread sees "Support" and keeps a letter. */}
            <Avatar className="w-11 h-11 border border-border">
              <AvatarFallback className="bg-muted text-muted-foreground text-sm tabular-nums">
                {chat.member_no != null
                  ? String(chat.member_no).padStart(3, '0')
                  : getInitials(chat.name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-baseline justify-between gap-2 mb-0.5">
                {/* The number, not the name: this list is open on a train. The
                    name is one tap away, in the conversation itself. */}
                <h3 className={`text-sm truncate tabular-nums ${chat.unread_count > 0 ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                  {chat.member_no != null ? `No. ${String(chat.member_no).padStart(3, '0')}` : (chat.name || 'Support')}
                </h3>
                {chat.last_message && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {formatDistanceToNow(new Date(chat.last_message.created_at), { addSuffix: true })}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <p className={`flex-1 text-sm truncate ${chat.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                  {chat.last_message?.message_type === 'capsule'
                    ? '🔒 Secure Capsule'
                    : (chat.last_message?.content || 'No messages yet')}
                </p>
                {chat.unread_count > 0 && (
                  <span
                    className="shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center"
                    data-testid={`unread-badge-${chat.id}`}
                  >
                    {chat.unread_count > 99 ? '99+' : chat.unread_count}
                  </span>
                )}
              </div>

              {chat.disappearing_messages_enabled && (
                <p className="text-xs text-secondary mt-1">
                  Disappearing messages enabled
                </p>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChatList;
